import { useEffect, useRef } from 'react';
import { accountsApi } from '../services/api/endpoints/accounts';
import { useAccountingStore } from '../stores/accountingStore';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import { useBusinessUnitStore } from '../stores/businessUnitStore';

const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Syncs the user's actual chart of accounts (for the current fiscal year)
 * into the global accounting store where AccountLookup can access it.
 * 
 * This replaces the old hardcoded SYSCOHADA_ACCOUNTS approach.
 * Call this once from a top-level component (e.g., Layout).
 */
export function useChartSync() {
  const { currentFiscalYear } = useFiscalYearStore();
  const { effectiveFilter } = useBusinessUnitStore();
  const { 
    chartFiscalYearId, 
    chartLastSynced, 
    setChartAccounts, 
    setChartLoading, 
    clearChart 
  } = useAccountingStore();
  const loadingRef = useRef(false);

  useEffect(() => {
    const fiscalYearId = currentFiscalYear?.id;
    if (!fiscalYearId) {
      clearChart();
      return;
    }

    // Skip if already cached and fresh for this FY
    const isCached = chartFiscalYearId === fiscalYearId && chartLastSynced;
    const isFresh = isCached && (Date.now() - chartLastSynced!) < CACHE_TTL_MS;
    if (isFresh || loadingRef.current) return;

    loadingRef.current = true;
    setChartLoading(true);

    // Load ALL accounts for this FY (large page size = no pagination)
    accountsApi.getAll({
      fiscalYear: fiscalYearId,
      pageSize: 5000,
      page: 1,
      businessUnitId: effectiveFilter.businessUnitId,
      businessUnitType: effectiveFilter.businessUnitType,
    })
      .then((response) => {
        if (response.success && response.data?.data) {
          setChartAccounts(response.data.data, fiscalYearId);
        }
      })
      .catch((err) => {
        console.error('Failed to sync chart of accounts:', err);
      })
      .finally(() => {
        setChartLoading(false);
        loadingRef.current = false;
      });
  }, [currentFiscalYear?.id, effectiveFilter.businessUnitId, effectiveFilter.businessUnitType]);
}
